import { Component, inject, OnInit, OnDestroy} from '@angular/core';
import { MsalBroadcastService, MsalGuardConfiguration, MsalService, MSAL_GUARD_CONFIG } from '@azure/msal-angular';
import { AuthenticationResult, EventMessage, EventType, InteractionStatus, InteractionType, PopupRequest, RedirectRequest } from '@azure/msal-browser';
import { Observable, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { createClaimsTable } from './shared/utils/claim-utils';
//import { pageRoles } from './app-routing.module';
import { NavigationProvider } from './app-routing.module';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { MatMenuModule } from '@angular/material/menu';
import { IdentityService } from './core/auth/identity.service';
import { LoadingService } from './core/http/loading.service';
//import { MenuItem } from './core/menu/menu.model';
//import { MenuService } from './core/menu/menu.service';
import { MenuComponent } from './core/menu/menu.component';
import { AlertComponent } from './core/alert/alert.component';
import { AuthStateService } from './core/auth/auth-state.service';

@Component({
    selector: 'app',
    standalone: true,
    imports: [CommonModule, MatMenuModule, RouterModule, MenuComponent, AlertComponent ],
    templateUrl: 'app.component.html',
    styleUrls: ['./app.component.css'],
})

export class AppComponent implements OnInit, OnDestroy{
    msalGuardConfig = inject(MSAL_GUARD_CONFIG) as unknown as MsalGuardConfiguration;
    authService = inject(MsalService);
    msalBroadcastService = inject(MsalBroadcastService);
    identityService = inject(IdentityService);
    loadingService = inject(LoadingService)
    authStateService = inject(AuthStateService);

    //loading$ = this.loadingService.loading$;

    isIframe = false;
    loginDisplay = false;
    roleDisplay = false;
    userName!: any;
    userRoles: any = [];
    private readonly _destroying$ = new Subject<void>();
    dataSource: any = [];
    routes: any;
    isLoading$: Observable<boolean> = this.loadingService.loading$; 

    private navigation = inject(NavigationProvider);
    // menu: MenuItem[] = [];
    
    async ngOnInit(): Promise<void> {  
        //this.isIframe = window !== window.parent && !window.opener;
        await this.identityService.initializeAuthConfig();
        await this.authService.instance.initialize();
        // ⏳ 1. Handle MSAL redirect result before anything else
        this.authService.instance.handleRedirectPromise().then((result: AuthenticationResult | null) => {
        if (result) {
            this.authService.instance.setActiveAccount(result.account);
        }
        this.setLoginDisplay(); // ✅ Safe to call after redirect is handled
        this.authService.instance.enableAccountStorageEvents();
        this.routes = this.navigation.getNavigationRoutes();
        // 2️⃣ Subscribe to ACCOUNT_ADDED / ACCOUNT_REMOVED events
        this.msalBroadcastService.msalSubject$.pipe(
            filter((msg: EventMessage) =>
            msg.eventType === EventType.ACCOUNT_ADDED ||
            msg.eventType === EventType.ACCOUNT_REMOVED
            )
        ).subscribe(() => {
            if (this.authService.instance.getAllAccounts().length === 0) {
            window.location.pathname = "/";
            } else {
            this.setLoginDisplay();
            }
        });
        // 3️⃣ Wait for MSAL interaction to finish before updating UI
        this.msalBroadcastService.inProgress$.pipe(
            filter((status: InteractionStatus) => status === InteractionStatus.None),
            takeUntil(this._destroying$)
        ).subscribe(() => {
            //this.checkAndSetActiveAccount();
            this.authStateService.isReady = true;
            });
            this.setLoginDisplay();
        })
        .catch(error => {
        console.error('MSAL redirect handling failed', error); });
    }   


    
    setLoginDisplay(): void {
        const account =
        this.authService.instance.getActiveAccount() ??
        this.authService.instance.getAllAccounts()[0];
        this.loginDisplay = !!account;
        this.userName = account?.idTokenClaims?.name ?? '';
        this.userRoles = (account?.idTokenClaims as any)?.roles ?? [];
    }

    // setLoginDisplay() {
    //     this.loginDisplay = this.authService.instance.getAllAccounts().length > 0;
    //     this.userName = this.authService.instance.getAllAccounts()[0]?.idTokenClaims?.name;
    //     this.userRoles = (this.authService.instance.getAllAccounts()[0]?.idTokenClaims?.roles);
    //     //this.menu = this.menuService.getMenu();
    // }

    // setRoleDisplay(page: string) {
    //     if (this.loginDisplay && this.userRoles != null) {
    //         const roles = pageRoles.find(e => e.page === page)?.roles;
    //         if(roles?.some((role: string) =>  this.userRoles.includes(role)))
    //         return true;
    //     }
    //     return false;
    // }

    // checkAndSetActiveAccount() {
    //     /**
    //      * If no active account set but there are accounts signed in, sets first account to active account
    //      * To use active account set here, subscribe to inProgress$ first in your component
    //      * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
    //      */
    //     const activeAccount = this.authService.instance.getActiveAccount();

    //     if (!activeAccount && this.authService.instance.getAllAccounts().length > 0) {
    //         const accounts = this.authService.instance.getAllAccounts();
    //         // add your code for handling multiple accounts here
    //         this.authService.instance.setActiveAccount(accounts[0]);
    //     }
    // }

    async login() {
        await this.authService.instance.initialize();         
        if (this.msalGuardConfig.interactionType === InteractionType.Popup) {
            if (this.msalGuardConfig.authRequest) {
                this.authService.loginPopup({ ...this.msalGuardConfig.authRequest } as PopupRequest)
                    .subscribe((response: AuthenticationResult) => {
                        this.authService.instance.setActiveAccount(response.account);
                    });
            } else {
                this.authService.loginPopup()
                    .subscribe((response: AuthenticationResult) => {
                        this.authService.instance.setActiveAccount(response.account);
                    });
            }
        } else {
            if (this.msalGuardConfig.authRequest) {
                this.authService.loginRedirect({ ...this.msalGuardConfig.authRequest } as RedirectRequest);
            } else {
                this.authService.loginRedirect();
            }
        }
    }

    logout() {
        const activeAccount = this.authService.instance.getActiveAccount() || this.authService.instance.getAllAccounts()[0];    
        if (this.msalGuardConfig.interactionType === InteractionType.Popup) {
            this.authService.logoutPopup({ account: activeAccount });
        } else {
            this.authService.logoutRedirect({ account: activeAccount });
        }
    }

    // unsubscribe to events when component is destroyed
    ngOnDestroy(): void {
        this._destroying$.next(undefined);
        this._destroying$.complete();
    }

    getClaims(claims: any) {
        if (claims) {
            const claimsTable = createClaimsTable(claims);
            this.dataSource = [...claimsTable];
        }
    }
}


